/**
 * EssayManagementAdmin - Centralized Essay Requirements Management
 *
 * Features:
 * - View all colleges for a student with essay status
 * - Bulk AI fetch for multiple colleges
 * - Individual actions: AI fetch, manual edit, sync slots, regenerate
 * - Filters by status, round, and search
 * - Progress tracking for bulk operations
 */

import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useFamilyContext } from '../../contexts/FamilyContext';
import EssaySlotSyncService from '../../services/EssaySlotSyncService';
import { toast } from 'react-hot-toast';
import {
  Sparkles,
  RefreshCw,
  Edit3,
  RotateCcw,
  Loader2,
  Users,
  CheckCircle2,
  AlertCircle,
  Circle,
  Search,
  Filter,
  X,
  ChevronDown,
  ChevronRight,
  Zap,
  FileText,
  Target,
  Clock
} from 'lucide-react';
import { format } from 'date-fns';

export default function EssayManagementAdmin() {
  const { familyUsers } = useFamilyContext();
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [colleges, setColleges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedColleges, setSelectedColleges] = useState([]);
  const [bulkOperating, setBulkOperating] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0, currentCollege: '' });
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all, no_essays, needs_sync, synced
  const [roundFilter, setRoundFilter] = useState('all'); // all, ed, ea, rd
  const [showFilters, setShowFilters] = useState(false);
  const [operatingColleges, setOperatingColleges] = useState(new Set());

  const children = familyUsers?.filter(user => user.type !== 'parent') || [];

  useEffect(() => {
    if (selectedUserId) {
      loadColleges();
    }
  }, [selectedUserId]);

  /**
   * Load colleges with essay requirements and sync status
   */
  const loadColleges = async () => {
    setLoading(true);
    try {
      // Fetch colleges for user
      const { data: collegeData, error: collegeError } = await supabase
        .from('college_journey_colleges')
        .select(`
          id,
          name,
          application_round,
          essay_requirements,
          created_at,
          updated_at
        `)
        .eq('user_id', selectedUserId)
        .order('name');

      if (collegeError) throw collegeError;

      // Fetch essay slots to determine sync status
      const { data: slots, error: slotsError } = await supabase
        .from('essay_slots')
        .select('id, college_id, prompt_text, status, updated_at')
        .eq('user_id', selectedUserId);

      if (slotsError) throw slotsError;

      // Fetch timeline tasks to check if tasks were generated
      const { data: tasks, error: tasksError } = await supabase
        .from('timeline_items')
        .select('id, college_id, source_type, source_id')
        .eq('user_id', selectedUserId)
        .eq('source_type', 'essay_slot');

      if (tasksError) throw tasksError;

      // Enhance college data with status
      const enhancedColleges = collegeData.map(college => {
        const essayReqs = college.essay_requirements || {};
        const essays = essayReqs.essays || [];
        const validEssays = essays.filter(e =>
          e.prompt && !e.prompt.toLowerCase().includes('not available')
        );

        const collegeSlots = slots?.filter(s => s.college_id === college.id) || [];
        const collegeTasks = tasks?.filter(t => t.college_id === college.id) || [];

        // Determine status
        let status = 'no_essays'; // No essays defined
        if (validEssays.length > 0) {
          if (collegeSlots.length === 0) {
            status = 'needs_sync'; // Essays defined but not synced
          } else if (collegeTasks.length === 0) {
            status = 'synced_no_tasks'; // Synced but no tasks generated
          } else {
            status = 'synced'; // Fully synced with tasks
          }
        }

        return {
          ...college,
          essayCount: validEssays.length,
          slotCount: collegeSlots.length,
          taskCount: collegeTasks.length,
          status,
          confidenceScore: essayReqs.confidence_score || null,
          verificationStatus: essayReqs.verification_status || null,
          lastSynced: collegeSlots.length > 0
            ? collegeSlots.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))[0]?.updated_at
            : null
        };
      });

      setColleges(enhancedColleges);
    } catch (error) {
      console.error('[EssayManagementAdmin] Error loading colleges:', error);
      toast.error('Failed to load colleges');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle AI fetch for a single college
   */
  const handleAIFetch = async (college) => {
    setOperatingColleges(prev => new Set(prev).add(college.id));
    const toastId = toast.loading(`Fetching essays for ${college.name}...`);

    try {
      const response = await fetch('/api/anthropic/fetch-essay-requirements', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          collegeName: college.name,
          collegeWebsite: college.website,
          applicationYear: '2025-2026'
        })
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch essays');
      }

      // Update the college with fetched data
      const { error: updateError } = await supabase
        .from('college_journey_colleges')
        .update({
          essay_requirements: data.essayRequirements,
          updated_at: new Date().toISOString()
        })
        .eq('id', college.id);

      if (updateError) throw updateError;

      toast.success(
        `Found ${data.essayRequirements.essays.length} essays (${data.essayRequirements.confidence_score}% confidence)`,
        { id: toastId }
      );

      // Reload colleges to show updated status
      await loadColleges();

    } catch (error) {
      console.error('[EssayManagementAdmin] AI fetch error:', error);
      toast.error(`Failed to fetch: ${error.message}`, { id: toastId });
    } finally {
      setOperatingColleges(prev => {
        const newSet = new Set(prev);
        newSet.delete(college.id);
        return newSet;
      });
    }
  };

  /**
   * Handle sync for a single college
   */
  const handleSync = async (college) => {
    setOperatingColleges(prev => new Set(prev).add(college.id));
    const toastId = toast.loading(`Syncing ${college.name}...`);

    try {
      const result = await EssaySlotSyncService.syncWithConfirmation(
        college.id,
        selectedUserId,
        true // auto-confirm
      );

      const { results } = result;
      const messages = [];
      if (results.created > 0) messages.push(`${results.created} created`);
      if (results.updated > 0) messages.push(`${results.updated} updated`);
      if (results.deleted > 0) messages.push(`${results.deleted} deleted`);
      if (results.preserved > 0) messages.push(`${results.preserved} preserved`);

      toast.success(
        messages.length > 0 ? messages.join(', ') : 'Already in sync',
        { id: toastId }
      );

      // Reload colleges
      await loadColleges();

    } catch (error) {
      console.error('[EssayManagementAdmin] Sync error:', error);
      toast.error(`Failed to sync: ${error.message}`, { id: toastId });
    } finally {
      setOperatingColleges(prev => {
        const newSet = new Set(prev);
        newSet.delete(college.id);
        return newSet;
      });
    }
  };

  /**
   * Handle regenerate (delete → AI fetch → sync)
   */
  const handleRegenerate = async (college) => {
    if (!confirm(`Regenerate essays for ${college.name}?\n\nThis will:\n1. Delete existing essay slots\n2. Fetch new essays from AI\n3. Create new slots and tasks\n\nDrafts will be preserved as unslotted.`)) {
      return;
    }

    setOperatingColleges(prev => new Set(prev).add(college.id));
    const toastId = toast.loading(`Regenerating ${college.name}...`);

    try {
      // Step 1: Delete existing slots (with preservation)
      const result = await EssaySlotSyncService.syncWithConfirmation(
        college.id,
        selectedUserId,
        true
      );

      // Step 2: AI fetch
      const response = await fetch('/api/anthropic/fetch-essay-requirements', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          collegeName: college.name,
          collegeWebsite: college.website,
          applicationYear: '2025-2026'
        })
      });

      const data = await response.json();
      if (!data.success) throw new Error(data.error || 'Failed to fetch essays');

      // Update college
      const { error: updateError } = await supabase
        .from('college_journey_colleges')
        .update({
          essay_requirements: data.essayRequirements,
          updated_at: new Date().toISOString()
        })
        .eq('id', college.id);

      if (updateError) throw updateError;

      // Step 3: Sync
      await EssaySlotSyncService.syncWithConfirmation(
        college.id,
        selectedUserId,
        true
      );

      toast.success(
        `Regenerated ${data.essayRequirements.essays.length} essays for ${college.name}`,
        { id: toastId }
      );

      await loadColleges();

    } catch (error) {
      console.error('[EssayManagementAdmin] Regenerate error:', error);
      toast.error(`Failed to regenerate: ${error.message}`, { id: toastId });
    } finally {
      setOperatingColleges(prev => {
        const newSet = new Set(prev);
        newSet.delete(college.id);
        return newSet;
      });
    }
  };

  /**
   * Handle bulk AI fetch
   */
  const handleBulkAIFetch = async () => {
    if (selectedColleges.length === 0) {
      toast.error('Please select colleges first');
      return;
    }

    if (!confirm(`Fetch essays for ${selectedColleges.length} colleges using AI?\n\nThis may take a few moments.`)) {
      return;
    }

    setBulkOperating(true);
    setBulkProgress({ current: 0, total: selectedColleges.length, currentCollege: '' });

    const selectedCollegeData = colleges.filter(c => selectedColleges.includes(c.id));
    let successCount = 0;
    let failCount = 0;

    for (let i = 0; i < selectedCollegeData.length; i++) {
      const college = selectedCollegeData[i];
      setBulkProgress({
        current: i + 1,
        total: selectedColleges.length,
        currentCollege: college.name
      });

      try {
        const response = await fetch('/api/anthropic/fetch-essay-requirements', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            collegeName: college.name,
            collegeWebsite: college.website,
            applicationYear: '2025-2026'
          })
        });

        const data = await response.json();

        if (data.success) {
          await supabase
            .from('college_journey_colleges')
            .update({
              essay_requirements: data.essayRequirements,
              updated_at: new Date().toISOString()
            })
            .eq('id', college.id);

          successCount++;
        } else {
          failCount++;
        }
      } catch (error) {
        console.error(`[EssayManagementAdmin] Bulk fetch failed for ${college.name}:`, error);
        failCount++;
      }
    }

    setBulkOperating(false);
    setBulkProgress({ current: 0, total: 0, currentCollege: '' });
    setSelectedColleges([]);

    toast.success(`Bulk fetch complete: ${successCount} succeeded, ${failCount} failed`);
    await loadColleges();
  };

  /**
   * Handle bulk sync
   */
  const handleBulkSync = async () => {
    if (selectedColleges.length === 0) {
      toast.error('Please select colleges first');
      return;
    }

    if (!confirm(`Sync essay slots for ${selectedColleges.length} colleges?`)) {
      return;
    }

    setBulkOperating(true);
    setBulkProgress({ current: 0, total: selectedColleges.length, currentCollege: '' });

    const selectedCollegeData = colleges.filter(c => selectedColleges.includes(c.id));
    let successCount = 0;
    let failCount = 0;

    for (let i = 0; i < selectedCollegeData.length; i++) {
      const college = selectedCollegeData[i];
      setBulkProgress({
        current: i + 1,
        total: selectedColleges.length,
        currentCollege: college.name
      });

      try {
        await EssaySlotSyncService.syncWithConfirmation(
          college.id,
          selectedUserId,
          true
        );
        successCount++;
      } catch (error) {
        console.error(`[EssayManagementAdmin] Bulk sync failed for ${college.name}:`, error);
        failCount++;
      }
    }

    setBulkOperating(false);
    setBulkProgress({ current: 0, total: 0, currentCollege: '' });
    setSelectedColleges([]);

    toast.success(`Bulk sync complete: ${successCount} succeeded, ${failCount} failed`);
    await loadColleges();
  };

  /**
   * Toggle college selection
   */
  const toggleCollegeSelection = (collegeId) => {
    setSelectedColleges(prev =>
      prev.includes(collegeId)
        ? prev.filter(id => id !== collegeId)
        : [...prev, collegeId]
    );
  };

  /**
   * Select/deselect all filtered colleges
   */
  const toggleSelectAll = () => {
    const filtered = getFilteredColleges();
    if (selectedColleges.length === filtered.length) {
      setSelectedColleges([]);
    } else {
      setSelectedColleges(filtered.map(c => c.id));
    }
  };

  /**
   * Get filtered colleges based on search and filters
   */
  const getFilteredColleges = () => {
    let filtered = colleges;

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(c =>
        c.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(c => c.status === statusFilter);
    }

    // Round filter
    if (roundFilter !== 'all') {
      const roundMap = { ed: 'early_decision', ea: 'early_action', rd: 'regular_decision' };
      filtered = filtered.filter(c => c.application_round === roundMap[roundFilter]);
    }

    return filtered;
  };

  /**
   * Get status badge component
   */
  const getStatusBadge = (status) => {
    const badges = {
      no_essays: {
        icon: Circle,
        text: 'No Essays',
        color: 'text-red-700 bg-red-100'
      },
      needs_sync: {
        icon: AlertCircle,
        text: 'Needs Sync',
        color: 'text-amber-700 bg-amber-100'
      },
      synced_no_tasks: {
        icon: AlertCircle,
        text: 'Synced (No Tasks)',
        color: 'text-orange-700 bg-orange-100'
      },
      synced: {
        icon: CheckCircle2,
        text: 'Synced',
        color: 'text-emerald-700 bg-emerald-100'
      }
    };

    const badge = badges[status] || badges.no_essays;
    const Icon = badge.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${badge.color}`}>
        <Icon className="w-3 h-3" />
        {badge.text}
      </span>
    );
  };

  const filteredColleges = getFilteredColleges();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-slate-900 mb-2">Essay Management</h2>
        <p className="text-slate-600">
          Centralized essay requirements management with bulk AI operations
        </p>
      </div>

      {/* Student Selection */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Users className="w-5 h-5 text-azure-600" />
          <h3 className="font-semibold text-slate-900">Select Student</h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {children.map(child => (
            <button
              key={child.id}
              onClick={() => setSelectedUserId(child.id)}
              className={`px-4 py-3 rounded-lg border-2 transition-all ${
                selectedUserId === child.id
                  ? 'border-azure-500 bg-azure-50 text-azure-900 font-semibold'
                  : 'border-slate-200 bg-white text-slate-700 hover:border-azure-300 hover:bg-azure-50'
              }`}
            >
              {child.name || child.displayName}
            </button>
          ))}
        </div>
      </div>

      {/* College Management */}
      {selectedUserId && (
        <>
          {/* Filters & Bulk Actions */}
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
            <div className="flex flex-col gap-4">
              {/* Top Row: Search & Filter Toggle */}
              <div className="flex items-center gap-3">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search colleges..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-azure-500"
                  />
                </div>
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                    showFilters
                      ? 'border-azure-500 bg-azure-50 text-azure-700'
                      : 'border-slate-300 text-slate-700 hover:border-azure-300'
                  }`}
                >
                  <Filter className="w-4 h-4" />
                  Filters
                  {showFilters ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </button>
              </div>

              {/* Filters */}
              {showFilters && (
                <div className="flex items-center gap-3 pt-3 border-t border-slate-200">
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-azure-500"
                  >
                    <option value="all">All Statuses</option>
                    <option value="no_essays">No Essays</option>
                    <option value="needs_sync">Needs Sync</option>
                    <option value="synced">Synced</option>
                  </select>

                  <select
                    value={roundFilter}
                    onChange={(e) => setRoundFilter(e.target.value)}
                    className="px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-azure-500"
                  >
                    <option value="all">All Rounds</option>
                    <option value="ed">Early Decision</option>
                    <option value="ea">Early Action</option>
                    <option value="rd">Regular Decision</option>
                  </select>

                  {(statusFilter !== 'all' || roundFilter !== 'all') && (
                    <button
                      onClick={() => {
                        setStatusFilter('all');
                        setRoundFilter('all');
                      }}
                      className="flex items-center gap-1 px-3 py-2 text-sm text-slate-600 hover:text-slate-900"
                    >
                      <X className="w-4 h-4" />
                      Clear
                    </button>
                  )}
                </div>
              )}

              {/* Bulk Actions */}
              {selectedColleges.length > 0 && (
                <div className="flex items-center gap-3 pt-3 border-t border-slate-200">
                  <span className="text-sm font-medium text-slate-700">
                    {selectedColleges.length} selected
                  </span>
                  <button
                    onClick={handleBulkAIFetch}
                    disabled={bulkOperating}
                    className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-lg hover:from-violet-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    <Sparkles className="w-4 h-4" />
                    Bulk AI Fetch
                  </button>
                  <button
                    onClick={handleBulkSync}
                    disabled={bulkOperating}
                    className="flex items-center gap-2 px-4 py-2 bg-azure-600 text-white rounded-lg hover:bg-azure-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Bulk Sync
                  </button>
                  <button
                    onClick={() => setSelectedColleges([])}
                    className="flex items-center gap-2 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50"
                  >
                    <X className="w-4 h-4" />
                    Clear Selection
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Bulk Progress */}
          {bulkOperating && (
            <div className="bg-azure-50 border border-azure-200 rounded-lg p-4">
              <div className="flex items-center gap-3 mb-2">
                <Loader2 className="w-5 h-5 text-azure-600 animate-spin" />
                <span className="font-medium text-azure-900">
                  Processing {bulkProgress.current} of {bulkProgress.total}...
                </span>
              </div>
              <p className="text-sm text-azure-700">{bulkProgress.currentCollege}</p>
              <div className="mt-3 w-full bg-azure-200 rounded-full h-2">
                <div
                  className="bg-azure-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(bulkProgress.current / bulkProgress.total) * 100}%` }}
                />
              </div>
            </div>
          )}

          {/* College List */}
          <div className="bg-white rounded-lg shadow-sm border border-slate-200">
            {/* Header */}
            <div className="p-4 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-slate-900">
                  Colleges ({filteredColleges.length})
                </h3>
                <button
                  onClick={toggleSelectAll}
                  className="text-sm text-azure-600 hover:text-azure-700 font-medium"
                >
                  {selectedColleges.length === filteredColleges.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>
            </div>

            {/* Loading */}
            {loading && (
              <div className="p-12 text-center">
                <Loader2 className="w-8 h-8 text-azure-600 animate-spin mx-auto mb-3" />
                <p className="text-slate-600">Loading colleges...</p>
              </div>
            )}

            {/* Empty State */}
            {!loading && filteredColleges.length === 0 && (
              <div className="p-12 text-center">
                <FileText className="w-12 h-12 text-slate-400 mx-auto mb-3" />
                <p className="text-slate-600 font-medium">No colleges found</p>
                <p className="text-sm text-slate-500 mt-1">
                  {searchQuery || statusFilter !== 'all' || roundFilter !== 'all'
                    ? 'Try adjusting your filters'
                    : 'This student has no colleges yet'}
                </p>
              </div>
            )}

            {/* College Table */}
            {!loading && filteredColleges.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider">
                        <input
                          type="checkbox"
                          checked={selectedColleges.length === filteredColleges.length}
                          onChange={toggleSelectAll}
                          className="rounded border-slate-300"
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider">
                        College
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider">
                        Round
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider">
                        Essays
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider">
                        Confidence
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-slate-700 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {filteredColleges.map(college => {
                      const isOperating = operatingColleges.has(college.id);

                      return (
                        <tr key={college.id} className="hover:bg-slate-50">
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              checked={selectedColleges.includes(college.id)}
                              onChange={() => toggleCollegeSelection(college.id)}
                              className="rounded border-slate-300"
                            />
                          </td>
                          <td className="px-4 py-3">
                            <div className="font-medium text-slate-900">{college.name}</div>
                            {college.lastSynced && (
                              <div className="text-xs text-slate-500 mt-0.5">
                                Synced {format(new Date(college.lastSynced), 'MMM d, h:mm a')}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {college.application_round && (
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-slate-100 text-slate-700">
                                {college.application_round === 'early_decision' && <Target className="w-3 h-3" />}
                                {college.application_round === 'early_action' && <Clock className="w-3 h-3" />}
                                {college.application_round === 'early_decision' && 'ED'}
                                {college.application_round === 'early_action' && 'EA'}
                                {college.application_round === 'regular_decision' && 'RD'}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {getStatusBadge(college.status)}
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-sm text-slate-900">
                              {college.essayCount} essay{college.essayCount !== 1 ? 's' : ''}
                            </div>
                            <div className="text-xs text-slate-500">
                              {college.slotCount} slot{college.slotCount !== 1 ? 's' : ''}, {college.taskCount} task{college.taskCount !== 1 ? 's' : ''}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            {college.confidenceScore && (
                              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                                college.confidenceScore >= 90
                                  ? 'bg-emerald-100 text-emerald-700'
                                  : college.confidenceScore >= 75
                                  ? 'bg-blue-100 text-blue-700'
                                  : college.confidenceScore >= 50
                                  ? 'bg-amber-100 text-amber-700'
                                  : 'bg-red-100 text-red-700'
                              }`}>
                                <Zap className="w-3 h-3" />
                                {college.confidenceScore}%
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => handleAIFetch(college)}
                                disabled={isOperating || bulkOperating}
                                className="p-2 text-violet-600 hover:bg-violet-50 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Fetch from AI"
                              >
                                {isOperating ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Sparkles className="w-4 h-4" />
                                )}
                              </button>
                              <button
                                onClick={() => handleSync(college)}
                                disabled={isOperating || bulkOperating || college.essayCount === 0}
                                className="p-2 text-azure-600 hover:bg-azure-50 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Sync Slots"
                              >
                                <RefreshCw className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleRegenerate(college)}
                                disabled={isOperating || bulkOperating}
                                className="p-2 text-orange-600 hover:bg-orange-50 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Regenerate"
                              >
                                <RotateCcw className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
